/**
 * RapidfireRoom — the spoken BLITZ round.
 *
 * Same realtime architecture as the AI interviewer:
 *   audio  : browser ↔ OpenAI over WebRTC (RTCPeerConnection + data channel)
 *   tools  : browser ↔ backend over the shared /ws/{session_id} WebSocket
 * Differences: the FRONTEND owns the per-question countdown (the AI never
 * waits on VAD after a timeout — we cancel, mark too_slow server-side via a
 * synthetic `client-` tool call, and nudge the model to move on), and the
 * BACKEND owns question ordering (skips are banked and re-enter later).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../styles/rapidfire.css';
import APIService from '../../services/APIService';
import { getIdToken } from '../../lib/firebase';
import { scopeIds } from './memoryLog';
import { friendlyVoiceError } from '../voiceError';
import InterviewPrelude from './InterviewPrelude';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BACKEND_WS_URL = BACKEND_URL.replace(/^http/, 'ws');

const ACCENTS = { tech: '#f97316', behavioral: '#a855f7' };
const FLASH = {
  correct: { label: 'CORRECT', color: '#34d399' },
  wrong: { label: 'MISS', color: '#f87171' },
  timeout: { label: 'TIME!', color: '#ef4444' },
  banked: { label: 'BANKED', color: '#fbbf24' },
};

const IcMic = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" />
  </svg>
);

// WebAudio cues — no asset files; one lazy context, simple oscillator envelopes.
let audioCtx = null;
function beep(kind) {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const t0 = audioCtx.currentTime;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain); gain.connect(audioCtx.destination);
    if (kind === 'buzzer') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(220, t0);
      osc.frequency.exponentialRampToValueAtTime(110, t0 + 0.4);
      gain.gain.setValueAtTime(0.12, t0);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.45);
      osc.start(t0); osc.stop(t0 + 0.45);
    } else if (kind === 'ding') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, t0);
      gain.gain.setValueAtTime(0.09, t0);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
      osc.start(t0); osc.stop(t0 + 0.18);
    } else {                                   // blip (banked / miss)
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(440, t0);
      gain.gain.setValueAtTime(0.08, t0);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.12);
      osc.start(t0); osc.stop(t0 + 0.12);
    }
  } catch { /* audio cues are best-effort */ }
}

const RapidfireRoom = ({ config, onComplete, onCancel }) => {
  const [status, setStatus] = useState('initializing'); // initializing|ready|running|completed|error
  const [error, setError] = useState('');
  const [card, setCard] = useState(null);        // {index,number,total,kind,question,topic,revisit}
  const [flipping, setFlipping] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [flash, setFlash] = useState(null);      // 'correct'|'wrong'|'timeout'|'banked'
  const [banked, setBanked] = useState(0);
  const [results, setResults] = useState({});    // index -> status (progress rail)
  const [totalQ, setTotalQ] = useState(0);
  const [micMuted, setMicMuted] = useState(false);
  const [aiLine, setAiLine] = useState('');      // last/streaming AI caption

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const audioElRef = useRef(null);
  const sessionIdRef = useRef(null);
  const timeLimitRef = useRef(25);
  const timerRef = useRef(null);
  const timerArmedRef = useRef(false);
  const cardRef = useRef(null);
  const startedAtRef = useRef(0);
  const messagesRef = useRef([]);                // [{role:'ai'|'user', text}]
  const aiPartialRef = useRef('');
  const clientCallRef = useRef(0);
  const completingRef = useRef(false);
  const doneFailsafeRef = useRef(null);
  const connectEpochRef = useRef(0);

  const dcSend = useCallback((obj) => {
    if (dcRef.current?.readyState === 'open') dcRef.current.send(JSON.stringify(obj));
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current); timerRef.current = null;
    setTimerRunning(false);
  }, []);

  const cleanup = useCallback(() => {
    stopTimer();
    clearTimeout(doneFailsafeRef.current);
    dcRef.current?.close(); dcRef.current = null;
    pcRef.current?.close(); pcRef.current = null;
    wsRef.current?.close(); wsRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current.srcObject = null; }
  }, [stopTimer]);

  const complete = useCallback(async () => {
    if (completingRef.current) return;
    completingRef.current = true;
    stopTimer();
    setStatus('completed');
    setTimeout(cleanup, 800);                    // let the last words play out
    try {
      const report = await APIService.rapidfireReport(sessionIdRef.current, {
        durationSeconds: startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0,
        messages: messagesRef.current.slice(-200),
      });
      onComplete?.(report);
    } catch (e) {
      console.warn('Rapidfire report failed:', e);
      setError({ title: 'Couldn’t build your report', retry: 'report',
        body: 'Your answers are safe — scoring just hiccuped. Give it another try.' });
      setStatus('error');
      completingRef.current = false;
    }
  }, [cleanup, onComplete, stopTimer]);

  // ── Countdown ──────────────────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    stopTimer();
    const limit = timeLimitRef.current;
    setSecondsLeft(limit);
    setTimerRunning(true);
    const endAt = Date.now() + limit * 1000;
    timerRef.current = setInterval(() => {
      const left = Math.max(0, (endAt - Date.now()) / 1000);
      setSecondsLeft(left);
      if (left <= 0) {
        stopTimer();
        onTimeout();                             // eslint-disable-line no-use-before-define
      }
    }, 250);
  }, [stopTimer]);                               // eslint-disable-line react-hooks/exhaustive-deps

  const sendClientTool = useCallback((name) => {
    const callId = `client-${name}-${++clientCallRef.current}`;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'tool_call', call_id: callId, name, arguments: {} }));
    }
  }, []);

  const onTimeout = useCallback(() => {
    if (!cardRef.current) return;
    beep('buzzer');
    setFlash('timeout');
    setResults((p) => ({ ...p, [cardRef.current.index]: 'too_slow' }));
    // Kill any in-flight VAD response + uncommitted speech, close server-side,
    // then push the model straight to the next question — no waiting on VAD.
    dcSend({ type: 'response.cancel' });
    dcSend({ type: 'input_audio_buffer.clear' });
    sendClientTool('rf_timeout');
    dcSend({
      type: 'conversation.item.create',
      item: {
        type: 'message', role: 'user',
        content: [{ type: 'input_text', text: '[TIMER] Time expired. Do not evaluate. Call rf_next_question() now.' }],
      },
    });
    dcSend({ type: 'response.create' });
  }, [dcSend, sendClientTool]);

  // ── ui_update from the backend state machine ───────────────────────────────
  const applyUiUpdate = useCallback((u) => {
    switch (u.action) {
      case 'rf_context':
        setTotalQ(u.total || 0);
        timeLimitRef.current = u.time_limit || 25;
        break;
      case 'rf_question': {
        stopTimer();
        setFlash(null);
        setFlipping(true);
        const next = { index: u.index, number: u.number, total: u.total, kind: u.kind, question: u.question, topic: u.topic, revisit: u.revisit };
        setTimeout(() => {
          cardRef.current = next;
          setCard(next);
          setSecondsLeft(timeLimitRef.current);
          setFlipping(false);
        }, 280);                                 // half the flip — swap at 90°
        timerArmedRef.current = true;            // starts when the AI stops talking
        if (!startedAtRef.current) startedAtRef.current = Date.now();
        setStatus('running');
        break;
      }
      case 'rf_verdict':
        stopTimer();
        setFlash(u.correct ? 'correct' : 'wrong');
        beep(u.correct ? 'ding' : 'blip');
        setResults((p) => ({ ...p, [u.index]: u.correct ? 'correct' : 'wrong' }));
        break;
      case 'rf_skipped':
        stopTimer();
        setFlash('banked');
        beep('blip');
        setBanked(u.banked_count || 0);
        setResults((p) => ({ ...p, [u.index]: 'banked' }));
        break;
      case 'rf_timeout':
        setResults((p) => ({ ...p, [u.index]: 'too_slow' }));
        break;
      case 'rf_done':
        // Failsafe: if the model never calls rf_conclude, finish anyway.
        clearTimeout(doneFailsafeRef.current);
        doneFailsafeRef.current = setTimeout(complete, 12000);
        break;
      case 'interview_concluded':
        complete();
        break;
      default:
        break;
    }
  }, [complete, stopTimer]);

  // ── OpenAI data-channel events ─────────────────────────────────────────────
  const handleDcEvent = useCallback((e) => {
    let ev;
    try { ev = JSON.parse(e.data); } catch { return; }
    switch (ev.type) {
      case 'response.audio_transcript.delta':
        aiPartialRef.current += ev.delta || '';
        setAiLine(aiPartialRef.current);
        break;
      case 'response.audio_transcript.done':
        if (aiPartialRef.current.trim()) messagesRef.current.push({ role: 'ai', text: aiPartialRef.current.trim() });
        aiPartialRef.current = '';
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (ev.transcript?.trim()) messagesRef.current.push({ role: 'user', text: ev.transcript.trim() });
        break;
      case 'output_audio_buffer.stopped':
        // The AI just finished SPEAKING — if a question is armed, the clock starts now.
        if (timerArmedRef.current && cardRef.current) {
          timerArmedRef.current = false;
          startTimer();
        }
        break;
      case 'response.function_call_arguments.done': {
        // Relay the model's tool call to the backend state machine.
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          let args = {};
          try { args = JSON.parse(ev.arguments || '{}'); } catch { /* ignore */ }
          wsRef.current.send(JSON.stringify({ type: 'tool_call', call_id: ev.call_id, name: ev.name, arguments: args }));
        }
        break;
      }
      default:
        break;
    }
  }, [startTimer]);

  // ── Connect ────────────────────────────────────────────────────────────────
  const connect = useCallback(async () => {
    cleanup();
    completingRef.current = false;
    const epoch = ++connectEpochRef.current;
    const stale = () => epoch !== connectEpochRef.current;
    setError('');
    setStatus('initializing');
    try {
      const conceptIds = scopeIds(config);
      const size = config?.options?.sessionSize || 5;
      const gen = await APIService.engineRapidfire({ conceptIds, count: Math.max(8, Math.min(20, size * 2)) });
      if (stale()) return;
      const questions = gen.questions || [];
      if (!questions.length) throw new Error('No questions could be generated for this scope — add topics in Session Settings.');

      const tokenData = await APIService.rapidfireToken({ questions, timeLimitSeconds: 25 });
      if (stale()) return;
      sessionIdRef.current = tokenData.session_id;
      timeLimitRef.current = tokenData.time_limit_seconds || 25;
      setTotalQ(tokenData.total_questions || questions.length);

      const wsToken = await getIdToken();
      const ws = new WebSocket(`${BACKEND_WS_URL}/ws/${tokenData.session_id}`
        + (wsToken ? `?token=${encodeURIComponent(wsToken)}` : ''));
      wsRef.current = ws;
      ws.onmessage = (me) => {
        let msg;
        try { msg = JSON.parse(me.data); } catch { return; }
        if (msg.type !== 'tool_response') return;
        if (msg.ui_update) applyUiUpdate(msg.ui_update);
        // Synthetic client- calls have no matching call on the model side —
        // forwarding their output would error the realtime session.
        if (String(msg.call_id || '').startsWith('client-')) return;
        dcSend({
          type: 'conversation.item.create',
          item: { type: 'function_call_output', call_id: msg.call_id, output: msg.result },
        });
        dcSend({ type: 'response.create' });
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      if (stale()) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      const audioEl = new Audio();
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      pc.ontrack = (te) => { if (te.track.kind === 'audio') audioEl.srcObject = te.streams[0]; };
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = handleDcEvent;
      dc.onopen = () => {
        if (stale()) return;
        dcSend({ type: 'session.update', session: tokenData.session_config });
        setStatus('ready');
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (stale()) return;
      const sdpResp = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST', body: offer.sdp,
        headers: { Authorization: `Bearer ${tokenData.ephemeral_key}`, 'Content-Type': 'application/sdp' },
      });
      if (stale()) return;
      if (!sdpResp.ok) throw new Error(`OpenAI WebRTC ${sdpResp.status}`);
      await pc.setRemoteDescription({ type: 'answer', sdp: await sdpResp.text() });
    } catch (err) {
      console.warn('Rapidfire connect failed:', err);
      if (!stale()) { setError(friendlyVoiceError(err)); setStatus('error'); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    connect();
    return () => { connectEpochRef.current++; cleanup(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (dcRef.current) dcRef.current.onmessage = handleDcEvent; }, [handleDcEvent]);

  const begin = useCallback(() => { beep('ding'); dcSend({ type: 'response.create' }); }, [dcSend]);
  const toggleMic = useCallback(() => {
    setMicMuted((m) => {
      streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = m; });
      return !m;
    });
  }, []);
  const endRound = useCallback(() => {
    if (Object.keys(results).length) complete();
    else { cleanup(); onCancel?.(); }
  }, [results, complete, cleanup, onCancel]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const limit = timeLimitRef.current;
  const urgent = timerRunning && secondsLeft <= 5;
  const accent = flash ? FLASH[flash].color : urgent ? '#ef4444' : ACCENTS[card?.kind] || ACCENTS.tech;
  const R = 26;
  const CIRC = 2 * Math.PI * R;
  const ringOffset = CIRC * (1 - (timerRunning ? secondsLeft / limit : 1));

  return (
    <div className="rf-root" style={{ '--mx': accent }}>
      <div className="rf-glow" />

      <div className="rf-topbar">
        <span className="rf-title">⚡ Rapidfire</span>
        <div className="rf-rail" aria-hidden="true">
          {Array.from({ length: totalQ }, (_, i) => (
            <span key={i} className={`rf-seg ${results[i] || ''}${card?.index === i ? ' cur' : ''}`} />
          ))}
        </div>
        <span className={`rf-banked${banked ? ' on' : ''}`}>Banked ×{banked}</span>
      </div>

      <div className="rf-stage">
        {status === 'initializing' && (
          <InterviewPrelude phase="preparing" variant="rapidfire" begin={false}
            captions={['Loading your rapid fire round.', 'Warming up the mic…']}
            sub="One-liners, about 25 seconds each, answered out loud." />
        )}
        {status === 'error' && (
          <div className="rf-center">
            <div className="rf-err-icon">{error?.kind === 'mic' ? '🎙' : '⚠'}</div>
            <h2 className="rf-err-title">{error?.title || 'Voice connection unavailable'}</h2>
            <p className="rf-error">{error?.body || 'Something interrupted the round — give it another try.'}</p>
            {error?.steps && (
              <ol className="rf-err-steps">
                {error.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            )}
            <div className="rf-row">
              <button type="button" className="rf-btn ghost" onClick={onCancel}>← Back</button>
              <button type="button" className="rf-btn primary"
                onClick={error?.retry === 'report' ? complete : connect}>Try again</button>
            </div>
          </div>
        )}
        {status === 'ready' && (
          <div className="rf-center">
            <h1 className="rf-h1">AI Rapidfire</h1>
            <p className="rf-sub">{totalQ} one-liners · {limit}s each · answer out loud.<br />
              Say <b>"skip"</b> to bank a question for later.</p>
            <button type="button" className="rf-btn primary big" onClick={begin}>Start the clock →</button>
          </div>
        )}
        {(status === 'running' || status === 'completed') && (
          <div className={`rf-card${flipping ? ' flip' : ''}`}>
            <div className="rf-card-inner">
              <div className="rf-card-head">
                <span className="rf-ringwrap" aria-label={`${Math.ceil(secondsLeft)} seconds left`}>
                  <svg viewBox="0 0 60 60" className="rf-ring">
                    <circle cx="30" cy="30" r={R} className="track" />
                    <circle cx="30" cy="30" r={R} className="arc"
                      strokeDasharray={CIRC} strokeDashoffset={ringOffset} />
                  </svg>
                  <span className={`rf-count${urgent ? ' urgent' : ''}`}>
                    {timerRunning ? Math.ceil(secondsLeft) : card?.number || ''}
                  </span>
                </span>
                <div className="rf-meta">
                  <span className="rf-kind">{card?.kind === 'behavioral' ? 'STAR · behavioral' : card?.topic || 'technical'}</span>
                  {card?.revisit && <span className="rf-revisit">from the bank</span>}
                </div>
                <span className="rf-qnum">Q{card?.number}<em>/{totalQ}</em></span>
              </div>
              <div className="rf-qtext">{card?.question || '…'}</div>
              {flash && (
                <div className="rf-flash" style={{ '--fc': FLASH[flash].color }} role="status">
                  {FLASH[flash].label}
                </div>
              )}
            </div>
          </div>
        )}
        {status === 'completed' && (
          <div className="rf-center overlay">
            <div className="rf-spinner" />
            <p>Building your report…</p>
          </div>
        )}
      </div>

      {aiLine && status === 'running' && <div className="rf-caption">{aiLine}</div>}

      <div className="rf-controls">
        <button type="button" className={`rf-btn dev${micMuted ? ' off' : ''}`} onClick={toggleMic}
          aria-pressed={micMuted} title={micMuted ? 'Unmute mic' : 'Mute mic'}>
          <IcMic /> {micMuted ? 'Muted' : 'Mic on'}
        </button>
        <button type="button" className="rf-btn end" onClick={endRound}>End round</button>
      </div>
    </div>
  );
};

export default RapidfireRoom;
